import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import api from '@/lib/api';

// Office-type roles that get automatic session-based timecard clock-in/out
// and inactivity auto-logout (TEMA D). Field roles (driller, pump_installer,
// operator, contractor) keep their existing manual flows untouched.
export const OFFICE_AUTO_TIMECARD_ROLES = ['admin', 'superadmin', 'office', 'manager'];

// How often to silently refresh the token while the app stays open, so a
// "remember me" session (30 days) keeps sliding forward for as long as the
// device is actually used — no daily re-login for drillers in the field.
const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6h

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const silentRefresh = useCallback(async () => {
    try {
      const res = await api.post('/auth/refresh');
      if (res.data?.token) {
        localStorage.setItem('bw_token', res.data.token);
      }
    } catch {
      // Non-fatal: the existing token keeps working until it actually expires.
    }
  }, []);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('bw_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      // Slide the session forward every time the app is opened with a
      // still-valid token — this is what makes "remember me" actually last.
      silentRefresh();
    } catch {
      localStorage.removeItem('bw_token');
      localStorage.removeItem('bw_user');
    } finally {
      setLoading(false);
    }
  }, [silentRefresh]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (!user) {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      return;
    }
    refreshTimerRef.current = setInterval(silentRefresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(refreshTimerRef.current);
  }, [user, silentRefresh]);

  const login = async (email, password, remember = true) => {
    const res = await api.post('/auth/login', { email, password, remember });
    localStorage.setItem('bw_token', res.data.token);
    localStorage.setItem('bw_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    // TEMA D: office-type roles get their clock-in registered automatically —
    // no need to remember to punch in. Fire-and-forget, never blocks login.
    if (OFFICE_AUTO_TIMECARD_ROLES.includes(res.data.user?.role)) {
      api.post('/timecards/auto-clock-in').catch(() => {});
    }
    return res.data;
  };

  const logout = () => {
    // Register the automatic clock-out BEFORE clearing the token, using the
    // token directly (not the shared `api` instance) so the request is
    // guaranteed to carry valid auth regardless of interceptor timing.
    const token = localStorage.getItem('bw_token');
    if (token && user && OFFICE_AUTO_TIMECARD_ROLES.includes(user.role)) {
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api`;
      axios.post(`${apiUrl}/timecards/auto-clock-out`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }
    localStorage.removeItem('bw_token');
    localStorage.removeItem('bw_user');
    setUser(null);
  };

  const hasPermission = (module, action) => {
    if (!user) return false;
    const role = user.role;
    if (role === 'superadmin') return true;
    const perms = user.permissions || {};
    const modulePerms = perms[module];
    if (modulePerms === undefined) return false;
    if (typeof modulePerms === 'boolean') return modulePerms;
    return !!modulePerms[action];
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, loadUser, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
